import React, { useEffect, useRef } from 'react';
import { OrdinarList } from './Lists/OrdinarList';
import { SystemList } from './Lists/SystemList';
import { ExpressList } from './Lists/ExpressList';
import { useTranslation } from 'react-i18next';
import {
    couponType,
    getPlayerSettings,
    MOBILE_MODAL_COUPON,
    SET_CURRENT_COUPON_TYPE,
    UPDATE_COUPON_TRANSFER_MESSAGE
} from '../../store/spirs/actions';
import { useDispatch, useSelector } from 'react-redux';
import { couponTypeArray, StoreType } from '../../store/spirs/types';
import { BottomBlock } from './Lists/components/BottomBlock';
import { ReactComponent as ExpandLessIcon } from '../CommonSportDisplay/icons/expand_less_w.svg';
import { CouponState } from '../../store/spirs/reducers/couponReducer';
import { CouponTabs } from '../tabs/CouponTabs';
import { ToastContainer, toast } from 'react-toastify';
import { useTestOverlay } from './TestOverlayContext';

export function Coupons() {
    const currentCouponType = useSelector(
        (state: StoreType) => state.spirs.coupons.currentCouponType
    );
    const currentEvents = useSelector(
        (state: StoreType) =>
            state.spirs.coupons[state.spirs.coupons.currentCouponType]
    );
    const transferMessage = useSelector(
        (state: StoreType) => state.spirs.coupons.transferMessage
    );
    const playerId = useSelector(
        (state: StoreType) => state.spirs.playerInfo.playerId
    );
    const {
        spirs: { isLoadingSettings }
    } = useSelector((state: StoreType) => state);

    const wasTransferredRef = useRef(false);
    const {
        showOverlay,
        countdown,
        isConnecting,
        initialTimeout
    } = useTestOverlay();

    const {t} = useTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        !isLoadingSettings && dispatch(getPlayerSettings.request({ playerId }));
    }, [isLoadingSettings, playerId, dispatch]);

    function handleChange(newCouponType: couponType) {
        dispatch(SET_CURRENT_COUPON_TYPE(newCouponType));
    }

    useEffect(() => {
        if (transferMessage) {
            toast.info(t('coupons.transfer'), {
                style: {
                    background: '#383E45',
                    color: '#fff',
                    width: '414px',
                    border: "2px solid #99FC6E33",
                    display: 'grid',
                    gridTemplateColumns: '40px 1fr',
                    alignItems: 'start',
                    fontSize: '14px'
                },
                onClose: () => {
                    // Сбрасываем флаг через небольшую задержку после закрытия
                    setTimeout(() => {
                        dispatch(UPDATE_COUPON_TRANSFER_MESSAGE(false))
                    }, 500);
                },
                position: 'top-center',
                autoClose: 3000,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                closeButton: false,
                hideProgressBar: true
            });
        }
    }, [transferMessage]);

    // object like: {EXPRESS: 33, ORDINAR: 10, SYSTEM: 21}
    const eventsCountByType = useSelector((state: StoreType) =>
        couponTypeArray.reduce(
            (ac, cT) => ({
                ...ac,
                [cT]: state.spirs.coupons[cT].length
            }),
            { EXPRESS: 0, ORDINAR: 0, SYSTEM: 0 }
        )
    );

    const tabs = couponTypeArray.map(couponType => ({
        label: t('coupons.' + couponType + '.tabTitle'),
        value: couponType as CouponState['currentCouponType'],
        badge: eventsCountByType[couponType]
    }));

    return (
        <div className="relative">
            <div className="pt-2.5 pl-2.5 grid grid-cols-[1fr,30px] gap-[10px] items-center">
                <CouponTabs
                    tabs={tabs}
                    value={currentCouponType}
                    onChange={handleChange}
                />
            </div>

            <div className="p-[10px]">
                {currentCouponType === 'ORDINAR' && (
                    <OrdinarList couponEvents={currentEvents} />
                )}
                {currentCouponType === 'EXPRESS' && (
                    <ExpressList couponEvents={currentEvents} />
                )}
                {currentCouponType === 'SYSTEM' && (
                    <SystemList couponEvents={currentEvents} />
                )}
                <div className="mt-[8px]">
                    <BottomBlock
                        emptyMessage={t(
                            'coupons.acceptBlock.emptyCouponMessage'
                        )}
                        showIt={currentEvents.length > 0}
                    />
                </div>
            </div>
            {/* Оверлей с прогресс-баром для обработки купона */}
            {(showOverlay || isConnecting) && !!countdown && (
                <div
                    className="absolute inset-0 flex items-center justify-center z-[8] pointer-events-auto"
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.64)' }}
                >
                    <div className="flex flex-col items-center space-y-4">
                        <div className="relative w-[120px] h-[120px]">
                            <svg
                                className="w-full h-full transform -rotate-90"
                                viewBox="0 0 120 120"
                            >
                                <circle
                                    cx="60"
                                    cy="60"
                                    r="54"
                                    stroke="rgba(39, 45, 51, 1)"
                                    strokeWidth="6"
                                    fill="transparent"
                                />
                                {showOverlay && countdown > 0 && (
                                    <circle
                                        cx="60"
                                        cy="60"
                                        r="54"
                                        stroke="#EACD40"
                                        strokeWidth="6"
                                        fill="transparent"
                                        strokeDasharray={`${2 * Math.PI * 54}`}
                                        strokeDashoffset={`${2 *
                                        Math.PI *
                                        54 *
                                        (1 -
                                            countdown /
                                            (initialTimeout || 20))}`}
                                        strokeLinecap="round"
                                        className="transition-all duration-1000 ease-linear"
                                    />
                                )}
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                {showOverlay && countdown ? (
                                    <span
                                        className="text-[16px] font-medium"
                                        style={{
                                            color: 'rgba(234, 205, 64, 1)'
                                        }}
                                    >
                                        {countdown}
                                    </span>
                                ) : (
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export const CouponView = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isOpenModal = useSelector(
        (state: StoreType) => state.mobileCouponModal
    );
    const [opened, setOpened] = React.useState(false);

    const handle = (state: boolean) => ()=> {
        if (state !== isOpenModal) {
            dispatch(MOBILE_MODAL_COUPON(state));
        }
        setOpened(state);
    };

    React.useEffect(() => {
        setOpened(isOpenModal);
    }, [isOpenModal])

    return (
        <div className="tablet:hidden bg-[#171a1e] fixed bottom-0 w-full z-[98] p-[8px] rounded-[12px] border-t border-[#0d83ba]">
            <div
                className="grid grid-cols-[1fr_50px] text-right items-center"
                onClick={handle(!opened)}
            >
                { isOpenModal ? <div>{t('closeCoupon')}</div> : <div>{t('goToCoupon')}</div> }
                <ExpandLessIcon />
            </div>
        </div>
    );
};
